/**
 * Data Hub — Pipeline Runs tab.
 *
 * Run list with per-phase expansion. Uses ExecBridge common Table (same pattern
 * as Staging / Production) and keeps Dataset + Reports columns.
 */
import { useMemo, useState } from "react";

import { ChevronDown, ChevronRight, FileText, Play } from "lucide-react";

import Button from "../../Components/Button";
import Table from "../../Components/Table";
import { useAppDispatch } from "../../Store/Hooks";
import { listDataHubLedgerRuns } from "../../Store/Reducers/DataHubConsoleSlice";
import { SyntheticBadge } from "../components/DatasetPill";
import { RunReportsDialog } from "../components/RunReportsDialog";
import { TabEmpty, TabError, TabLoading } from "../components/TabState";
import { isIncompleteLedgerRun } from "../data/api";
import { DATASET_LABELS } from "../data/dataset";
import { RO_SCORES_ENABLED, isRoScoresPhase } from "../data/flags";
import { pipelineRunIdFromLedger } from "../data/resume";
import type {
  PhaseResult,
  PhaseStatus,
  PipelineRun,
  RunStatus,
} from "../data/types";
import { useActiveDataset } from "../hooks/useActiveDataset";
import { useAdapterData } from "../hooks/useAdapterData";
import { useDataHubAdapter } from "../hooks/useDataHubAdapter";

/** Fallback grouping for runs recorded before the server sent `group`. */
const PHASE_2_STEPS = new Set([
  "benchmark",
  "normalize",
  "summarize",
  "kpi_scores",
  "ro_scores",
]);

/**
 * "running" in the ledger means "opened and not finished". Nothing executes
 * in the background — Phase 2 runs inside the browser session — so a run
 * listed here as running was left part-way, and reading "Running" for hours
 * suggested something was stuck.
 */
const RUN_STATUS_LABELS: Partial<Record<RunStatus, string>> = {
  running: "Not finished",
};
const RUN_STATUS_TITLES: Partial<Record<RunStatus, string>> = {
  running:
    "Left before it finished. Resume it to carry on, or open it and abandon it.",
};

const RUN_STATUS_STYLES: Record<RunStatus, string> = {
  succeeded: "bg-green-100 text-green-800",
  partial: "bg-amber-100 text-amber-900",
  failed: "bg-red-100 text-red-800",
  running: "bg-amber-50 text-amber-800",
};

const PHASE_STATUS_STYLES: Record<PhaseStatus, string> = {
  success: "text-green-700",
  failed: "text-red-700",
  skipped: "text-gray-400",
  running: "text-blue-700",
};

/**
 * Step names as the console shows them. `cleanse`/`commit` are the
 * orchestrator's spellings for QC and Promote; a run may carry either, so both
 * map to the name the user saw in the wizard.
 */
const PHASE_LABELS: Record<string, string> = {
  file: "File",
  columns: "Columns",
  accounts: "Accounts",
  metrics: "Metrics",
  categories: "Categories",
  import: "Import",
  qc: "QC",
  cleanse: "QC",
  heal: "Heal",
  promote: "Promote",
  commit: "Promote",
  benchmark: "Benchmark",
  normalize: "Normalize",
  summarize: "Summarize",
  kpi_scores: "Category Scores",
  ro_scores: "R&O Scores",
};

/**
 * The Detail column, built from whatever the step recorded.
 *
 * A step with nothing to report says so in italics rather than rendering an
 * empty cell — the reference does the same, and a blank cell reads as a bug.
 */
function detailFor(p: PhaseResult): { text: string; muted: boolean } {
  const bits: string[] = [];
  const e = (p.extras ?? {}) as Record<string, any>;

  if (typeof e.sheet === "string") bits.push(`Source: ${e.sheet}`);
  if (typeof e.columns === "number") bits.push(`${e.columns} columns`);

  (
    [
      ["auto_matched", "auto-matched"],
      ["user_matched", "user-matched"],
      ["ignored", "ignored"],
      ["skipped", "skipped"],
    ] as const
  ).forEach(([key, label]) => {
    if (typeof e[key] === "number") bits.push(`${e[key]} ${label}`);
  });

  if (typeof e.rows_reviewed === "number")
    bits.push(`${e.rows_reviewed} reviewed`);
  if (typeof e.rows_fixed === "number") bits.push(`${e.rows_fixed} fixed`);

  if (p.count != null && bits.length === 0) {
    bits.push(`count: ${p.count.toLocaleString()}`);
  } else if (p.count != null) {
    bits.unshift(`count: ${p.count.toLocaleString()}`);
  }

  if (p.error) bits.push(p.error);
  if (p.reason) bits.push(p.reason);

  if (bits.length === 0) {
    return { text: "No detail recorded for this step.", muted: true };
  }
  return { text: bits.join(" · "), muted: false };
}

function formatDuration(ms: number | null) {
  if (ms == null) return "—";
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s`;
  return `${Math.floor(s / 60)}m ${s % 60}s`;
}

function formatWhen(iso: string) {
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function phaseGroup(p: PhaseResult): "phase_1" | "phase_2" {
  if (p.group === "phase_1" || p.group === "phase_2") return p.group;
  return PHASE_2_STEPS.has(p.phase) ? "phase_2" : "phase_1";
}

function RunPhasesDetail({ run }: { run: PipelineRun }) {
  const phases = (run.phases || []).filter(
    p => RO_SCORES_ENABLED || !isRoScoresPhase(p.phase)
  );
  if (!phases.length) {
    return <div className="text-xs text-gray-500">No phase detail.</div>;
  }

  return (
    <div className="-mx-4 -my-4 divide-y divide-gray-200">
      {(["phase_1", "phase_2"] as const).map(group => {
        const inGroup = phases.filter(p => phaseGroup(p) === group);
        if (!inGroup.length) return null;
        return (
          <div key={group}>
            <div className="bg-gray-100 px-4 py-1 text-[10px] font-semibold uppercase tracking-wide text-gray-500">
              {group === "phase_1" ? "Phase 1" : "Phase 2"}
            </div>
            {inGroup.map((p, i) => {
              const detail = detailFor(p);
              return (
                <div
                  key={`${p.phase}-${i}`}
                  className="flex flex-col gap-1 px-4 py-2 sm:flex-row sm:items-center sm:justify-between"
                >
                  <div className="flex items-center gap-3">
                    <span className="min-w-[150px] text-sm font-medium text-gray-900">
                      {PHASE_LABELS[p.phase] ?? p.phase}
                    </span>
                    <span
                      className={`text-xs font-semibold ${PHASE_STATUS_STYLES[p.status] || "text-gray-500"}`}
                    >
                      {p.status}
                    </span>
                  </div>
                  <div
                    className={`text-xs ${
                      detail.muted ? "italic text-gray-400" : "text-gray-600"
                    }`}
                  >
                    {detail.text}
                  </div>
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}

type RunRow = PipelineRun & {
  ledgerRunId?: string;
  origin: "pipeline";
};

type Props = {
  onResume?: (ledgerRunId: string) => void;
  /** Ledger run id currently being resumed; only that row shows a spinner. */
  resumeBusy?: string | null;
};

export function PipelineRunsTab({ onResume, resumeBusy }: Props) {
  const [dataset] = useActiveDataset();
  // Refetch when the pill changes — fetchRuns scopes to the active dataset.
  const dispatch = useAppDispatch();
  const adapter = useDataHubAdapter();
  const { data, loading, error } = useAdapterData(
    () => adapter.getRuns(),
    [dataset]
  );
  const { data: ledgerRuns } = useAdapterData(
    () =>
      dispatch(listDataHubLedgerRuns({ dataset }))
        .unwrap()
        .catch(() => []),
    [dataset]
  );
  const [expandedKeys, setExpandedKeys] = useState<Array<string | number>>([]);
  // The run whose reports are open. Reaching them from the row is the point —
  // otherwise finding out what a run did means picking it again from a list
  // you are already looking at.
  const [reportsFor, setReportsFor] = useState<string | null>(null);
  const pipelineRuns = data ?? [];

  // Show pipeline runs only. Incomplete ledger rows used to appear as a second
  // "Partial" line per upload; keep them off the table, but still attach a
  // linked ledger id so Resume can work when import recorded pipeline_run_id.
  const runs: RunRow[] = useMemo(() => {
    const byPipelineId = new Map<string, string>();
    for (const lr of ledgerRuns ?? []) {
      if (!isIncompleteLedgerRun(lr)) continue;
      const pid = pipelineRunIdFromLedger(lr);
      if (pid) byPipelineId.set(pid, lr.id);
    }
    return pipelineRuns.map((r, i) => ({
      ...r,
      run_no: r.run_no ?? pipelineRuns.length - i,
      ledgerRunId: byPipelineId.get(r.id),
      origin: "pipeline" as const,
    }));
  }, [ledgerRuns, pipelineRuns]);

  const toggleExpand = (id: string) => {
    setExpandedKeys(prev => (prev.includes(id) ? [] : [id]));
  };

  const columns = useMemo(
    () => [
      {
        key: "expand",
        title: "",
        width: "48px",
        render: (_: unknown, record: PipelineRun) => {
          const isOpen = expandedKeys.includes(record.id);
          return (
            <button
              type="button"
              className="inline-flex items-center justify-center rounded p-0.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
              aria-label={isOpen ? "Collapse run" : "Expand run"}
              onClick={e => {
                e.stopPropagation();
                toggleExpand(record.id);
              }}
            >
              {isOpen ? (
                <ChevronDown className="h-4 w-4" />
              ) : (
                <ChevronRight className="h-4 w-4" />
              )}
            </button>
          );
        },
      },
      {
        key: "run_no",
        title: "Run",
        width: "80px",
        render: (_: unknown, record: PipelineRun) => (
          <span className="font-medium text-gray-900">#{record.run_no}</span>
        ),
      },
      {
        key: "started_at",
        title: "When",
        width: "170px",
        render: (value: string) => (
          <span className="text-gray-600">{formatWhen(value)}</span>
        ),
      },
      {
        key: "source",
        title: "Source",
        render: (value: string | null) => (
          <span className="block truncate text-gray-600" title={value ?? ""}>
            {value ?? "—"}
          </span>
        ),
      },
      // Kept as a confirmation column even though the list is filtered to the
      // active pill — makes a mis-scoped row visible if filtering ever fails.
      {
        key: "dataset_kind",
        title: "Dataset",
        width: "120px",
        render: (_: unknown, record: PipelineRun) =>
          record.dataset_kind === "synthetic" ? (
            <SyntheticBadge dataset={record.dataset_kind} />
          ) : (
            <span className="text-gray-600">production</span>
          ),
      },
      {
        key: "duration_ms",
        title: "Duration",
        width: "100px",
        render: (value: number | null) => (
          <span className="text-gray-600">{formatDuration(value)}</span>
        ),
      },
      {
        key: "status",
        title: "Status",
        width: "120px",
        render: (value: RunStatus) => (
          <span
            title={RUN_STATUS_TITLES[value]}
            className={`inline-flex items-center whitespace-nowrap rounded-md px-2.5 py-0.5 text-xs font-semibold capitalize ${RUN_STATUS_STYLES[value] || "bg-gray-100 text-gray-700"}`}
          >
            {RUN_STATUS_LABELS[value] ?? value}
          </span>
        ),
      },
      {
        key: "phases",
        title: "Phases",
        width: "90px",
        textalign: "left" as const,
        render: (_: unknown, record: PipelineRun) => {
          const ok = record.phases.filter(p => p.status === "success").length;
          return (
            <span className="text-gray-600">
              {ok}/{record.phases.length}
            </span>
          );
        },
      },
      {
        key: "report",
        title: "Report",
        width: "120px",
        textalign: "left" as const,
        render: (_: unknown, record: RunRow) => {
          const canReports = record.origin === "pipeline";
          return (
            <Button
              type="button"
              variant="outline-primary"
              className="!px-2.5 !py-1 text-xs"
              icon={<FileText className="h-3.5 w-3.5" />}
              disabled={!canReports}
              title={
                canReports
                  ? "View reports for this run"
                  : "Reports available after the run is recorded in the pipeline"
              }
              onClick={e => {
                e.stopPropagation();
                if (!canReports) return;
                setReportsFor(record.id);
              }}
            >
              Reports
            </Button>
          );
        },
      },
      {
        key: "actions",
        title: "Actions",
        width: "120px",
        textalign: "left" as const,
        render: (_: unknown, record: RunRow) => {
          // Succeeded runs are done — Resume would re-open a finished wizard.
          const canResume = Boolean(
            record.ledgerRunId && onResume && record.status !== "succeeded"
          );
          const isResuming =
            Boolean(resumeBusy) && resumeBusy === record.ledgerRunId;
          return (
            <Button
              type="button"
              variant="primary"
              className="!px-2.5 !py-1 text-xs"
              icon={<Play className="h-3.5 w-3.5" />}
              loading={isResuming}
              disabled={!canResume || Boolean(resumeBusy)}
              title={
                canResume
                  ? "Resume this incomplete run"
                  : "Resume is only available for incomplete runs"
              }
              onClick={e => {
                e.stopPropagation();
                if (!canResume || !record.ledgerRunId || !onResume) return;
                onResume(record.ledgerRunId);
              }}
            >
              Resume
            </Button>
          );
        },
      },
    ],
    [expandedKeys, onResume, resumeBusy]
  );

  if (loading && !runs.length)
    return <TabLoading label="Loading pipeline runs…" />;
  if (error) return <TabError error={error} />;
  if (!runs.length)
    return (
      <TabEmpty
        message={`No ${DATASET_LABELS[dataset].toLowerCase()} runs recorded for this organization yet.`}
      />
    );

  return (
    <div>
      <RunReportsDialog
        open={reportsFor !== null}
        onOpenChange={open => !open && setReportsFor(null)}
        runId={reportsFor}
      />
      <div className="border-b border-gray-200 px-4 py-3">
        <h3 className="m-0 text-base font-semibold text-gray-900">
          Pipeline runs
        </h3>
        <p className="m-0 text-xs text-gray-500">{runs.length} recorded</p>
      </div>

      <Table
        className="table-fixed [&_th]:normal-case [&_th]:tracking-normal"
        columns={columns}
        data={runs}
        loading={loading && !runs.length}
        rowKey={record => record.id}
        expandedRowKeys={expandedKeys}
        expandedRowRender={record => <RunPhasesDetail run={record} />}
        emptyContent={
          <p className="m-0 text-sm text-gray-500">
            No {DATASET_LABELS[dataset].toLowerCase()} runs recorded for this
            organization yet.
          </p>
        }
      />
    </div>
  );
}

export default PipelineRunsTab;
